import {createAsyncThunk} from '@reduxjs/toolkit';
import {AxiosResponse} from 'axios';
//enum
import {ACTION_TYPE} from '../actions-types';
import {STORAGE_KEY} from '@/utils/enums/storage.enum';
//types
import {
	IOrganization,
	IOrganizationLoginRequest,
	IOrganizationResponse,
	IOrganizationChangeProfileRequest,
	IOrganizationSignUpResponse,
	ITeamData,
	ITeamUpdateData,
	IAuthAppleRequest,
	IAuthGoogleRequest,
} from '../../utils/types';
//services
import {OrganizationService} from '@/utils/services';
import {orgStorage} from '@/utils/services/storage.service';
//helpers
import {getErrorResponse} from '@/utils/helpers';

const createOrganizationSignup = <T>(
	actionType: ACTION_TYPE,
	register: (request: T) => Promise<AxiosResponse<IOrganizationSignUpResponse>>
) => createAsyncThunk(actionType, async (reqBody: T, {rejectWithValue}) => {
	try {
		const {data} = await register(reqBody);
		orgStorage.set(STORAGE_KEY.JWT_BEARER_TOKEN, data.tokens.jwtBearerToken);
		orgStorage.set(STORAGE_KEY.REFRESH_TOKEN, data.tokens.refreshToken);
		orgStorage.set(STORAGE_KEY.ORGANIZATION_ID, data.organization.id);
		orgStorage.set(STORAGE_KEY.ORGANIZATION_ROLE, 'Owner');
		return data.organization;
	} catch (error) {
		return rejectWithValue(getErrorResponse(error));
	}
});

export const organizationSignup = createOrganizationSignup(
	ACTION_TYPE.ORGANIZATION_SIGNUP, OrganizationService.create
);

export const organizationGoogleSignup = createOrganizationSignup(
	ACTION_TYPE.ORGANIZATION_GOOGLE_SIGNUP, OrganizationService.googleRegister
);

export const organizationAppleSignup = createOrganizationSignup(
	ACTION_TYPE.ORGANIZATION_APPLE_SIGNUP, OrganizationService.appleRegister
);

export const organizationLogin = createAsyncThunk(
	ACTION_TYPE.ORGANIZATION_LOGIN,
	async (reqBody: IOrganizationLoginRequest, {rejectWithValue}) => {
		try {
			const organizationResponse: AxiosResponse<IOrganizationResponse> = await OrganizationService.login(reqBody);

			if (organizationResponse.data.organization) {
				orgStorage.set(STORAGE_KEY.JWT_BEARER_TOKEN, organizationResponse.data.tokens.jwtBearerToken);
				orgStorage.set(STORAGE_KEY.REFRESH_TOKEN, organizationResponse.data.tokens.refreshToken);
				orgStorage.set(STORAGE_KEY.ORGANIZATION_ID, organizationResponse.data.organization.id);
				orgStorage.set(STORAGE_KEY.ORGANIZATION_ROLE, 'Owner');

				return organizationResponse.data;
			}

			orgStorage.set(STORAGE_KEY.JWT_BEARER_TOKEN, organizationResponse.data.tokens.jwtBearerToken);
			orgStorage.set(STORAGE_KEY.REFRESH_TOKEN, organizationResponse.data.tokens.refreshToken);
			orgStorage.set(STORAGE_KEY.ORGANIZATION_ID, organizationResponse.data.team.organizationId);
			orgStorage.set(STORAGE_KEY.TEAM_MEMBER_ID, organizationResponse.data.team.id);
			orgStorage.set(STORAGE_KEY.ORGANIZATION_ROLE, organizationResponse.data.team.role);

			return organizationResponse.data;
		} catch (error) {
			return rejectWithValue(getErrorResponse(error));
		}
	}
);

export const organizationLogout = createAsyncThunk(ACTION_TYPE.ORGANIZATION_LOGOUT, () => orgStorage.clear());

export const getOrganizationInfo = createAsyncThunk(
	ACTION_TYPE.ORGANIZATION_GET_INFO,
	async (id: number, {rejectWithValue}) => {
		try {
			const organizationResponse: AxiosResponse<IOrganization> = await OrganizationService.getOrganizationInfo(id);

			return organizationResponse.data;
		} catch (error) {
			return rejectWithValue(getErrorResponse(error));
		}
	}
);

export const changeProfile = createAsyncThunk(
	ACTION_TYPE.CHANGE_ORGANIZATION_PROFILE,
	async (reqBody: IOrganizationChangeProfileRequest, {rejectWithValue}) => {
		try {
			const organizationResponse: AxiosResponse<IOrganization> = await OrganizationService.changeProfile(reqBody);

			return organizationResponse.data;
		} catch (error) {
			return rejectWithValue(getErrorResponse(error));
		}
	}
);

export const getTeamMemberInfo = createAsyncThunk(
	ACTION_TYPE.TEAM_GET_TEAM_MEMBER_INFO,
	async (id: number, {rejectWithValue}) => {
		try {
			const teamResponse: AxiosResponse<ITeamData> = await OrganizationService.getTeamMemberInfo(id);

			return teamResponse.data;
		} catch (error) {
			return rejectWithValue(getErrorResponse(error));
		}
	}
);
// export const updateTeamMember = createAsyncThunk(
// 	ACTION_TYPE.TEAM_UPDATE_TEAM_MEMBER,
// 	async (reqBody: ITeamUpdateData, {rejectWithValue}) => {
// 		try {
// 			const teamResponse: AxiosResponse<ITeamData> = await OrganizationService.updateTeamMember(reqBody);

// 			return teamResponse.data;
// 		} catch (error) {
// 			return rejectWithValue(getErrorResponse(error));
// 		}
// 	}
// );

export const authGoogle = createAsyncThunk(
	ACTION_TYPE.ORGANIZATION_LOGIN,
	async (reqBody: IAuthGoogleRequest, {rejectWithValue}) => {
		try {
			const organizationResponse: AxiosResponse<IOrganizationResponse> = await OrganizationService.googleAuth(reqBody);

			if (organizationResponse.data.organization) {
				orgStorage.set(STORAGE_KEY.JWT_BEARER_TOKEN, organizationResponse.data.tokens.jwtBearerToken);
				orgStorage.set(STORAGE_KEY.REFRESH_TOKEN, organizationResponse.data.tokens.refreshToken);
				orgStorage.set(STORAGE_KEY.ORGANIZATION_ID, organizationResponse.data.organization.id);
				orgStorage.set(STORAGE_KEY.ORGANIZATION_ROLE, 'Owner');

				return organizationResponse.data;
			}

			orgStorage.set(STORAGE_KEY.JWT_BEARER_TOKEN, organizationResponse.data.tokens.jwtBearerToken);
			orgStorage.set(STORAGE_KEY.REFRESH_TOKEN, organizationResponse.data.tokens.refreshToken);
			orgStorage.set(STORAGE_KEY.ORGANIZATION_ID, organizationResponse.data.team.organizationId);
			orgStorage.set(STORAGE_KEY.TEAM_MEMBER_ID, organizationResponse.data.team.id);
			orgStorage.set(STORAGE_KEY.ORGANIZATION_ROLE, organizationResponse.data.team.role);

			return organizationResponse.data;
		} catch (error) {
			return rejectWithValue(getErrorResponse(error));
		}
	}
);

export const authApple = createAsyncThunk(
	ACTION_TYPE.ORGANIZATION_LOGIN,
	async (reqBody: IAuthAppleRequest, {rejectWithValue}) => {
		try {
			const organizationResponse: AxiosResponse<IOrganizationResponse> = await OrganizationService.appleAuth(reqBody);

			if (organizationResponse.data.organization) {
				orgStorage.set(STORAGE_KEY.JWT_BEARER_TOKEN, organizationResponse.data.tokens.jwtBearerToken);
				orgStorage.set(STORAGE_KEY.REFRESH_TOKEN, organizationResponse.data.tokens.refreshToken);
				orgStorage.set(STORAGE_KEY.ORGANIZATION_ID, organizationResponse.data.organization.id);
				orgStorage.set(STORAGE_KEY.ORGANIZATION_ROLE, 'Owner');

				return organizationResponse.data;
			}

			orgStorage.set(STORAGE_KEY.JWT_BEARER_TOKEN, organizationResponse.data.tokens.jwtBearerToken);
			orgStorage.set(STORAGE_KEY.REFRESH_TOKEN, organizationResponse.data.tokens.refreshToken);
			orgStorage.set(STORAGE_KEY.ORGANIZATION_ID, organizationResponse.data.team.organizationId);
			orgStorage.set(STORAGE_KEY.TEAM_MEMBER_ID, organizationResponse.data.team.id);
			orgStorage.set(STORAGE_KEY.ORGANIZATION_ROLE, organizationResponse.data.team.role);

			return organizationResponse.data;
		} catch (error) {
			return rejectWithValue(getErrorResponse(error));
		}
	}
);
